// src/shared/components/DurationFormInput.jsx
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import FormInput from './FormInput';

/**
 * A specialized input component for duration values that supports human-readable formats.
 * 
 * Supported formats: 3d, 2h, 30min, 1mo, 2w, 45s
 * Units: s (seconds), min (minutes), h (hours), d (days), w (weeks), mo (months), y (years)
 * 
 * Two modes:
 * - 'string': passes the duration string as-is (for backend timestamp calculation)
 * - 'seconds': converts to/from seconds internally (for workflow durations)
 */
export default function DurationFormInput({
  id,
  label,
  value,
  onChange,
  placeholder,
  helperText,
  required,
  disabled,
  mode, // 'string' or 'seconds'
  ...rest
}) {
  const [displayValue, setDisplayValue] = useState('');
  const [error, setError] = useState('');

  // When component receives a new value from parent, convert it for display
  useEffect(() => {
    if (mode === 'seconds' && value && typeof value === 'number') {
      // Convert seconds to human-readable format
      setDisplayValue(formatSecondsToHuman(value));
    } else if (mode === 'string' && value) {
      // Just display the string as-is
      setDisplayValue(value);
    } else if (!value) {
      setDisplayValue('');
    }
  }, [value, mode]);

  const handleChange = (e) => {
    const input = e.target.value;
    setDisplayValue(input);

    // Clear error when user starts typing
    if (error) setError('');

    // Don't validate empty values if not required
    if (!input && !required) {
      onChange(mode === 'seconds' ? null : '');
      return;
    }

    // Validate format
    if (input && !validateDurationFormat(input)) {
      setError('Invalid format. Use: 3d, 2h, 30min, 1mo, 2w, 45s, 1y');
      // Don't call onChange - keep the invalid value in display but don't update parent
      return;
    }

    // If valid, convert and pass to parent
    if (input) {
      if (mode === 'seconds') {
        const seconds = parseToSeconds(input);
        onChange(seconds);
      } else {
        // mode === 'string'
        onChange(input);
      }
    } else {
      onChange(mode === 'seconds' ? null : '');
    }
  };

  const defaultHelperText = (
    <>
      Format: <strong>3d</strong> (days), <strong>2h</strong> (hours), <strong>30min</strong> (minutes), 
      <strong> 1mo</strong> (months), <strong>2w</strong> (weeks), <strong>45s</strong> (seconds), <strong>1y</strong> (years)
    </>
  );

  return (
    <FormInput
      id={id}
      label={label}
      value={displayValue}
      onChange={handleChange}
      placeholder={placeholder || 'e.g., 3d, 2h, 30min'}
      helperText={helperText || defaultHelperText}
      error={error}
      required={required}
      disabled={disabled}
      {...rest}
    />
  );
}

DurationFormInput.propTypes = {
  id: PropTypes.string.isRequired,
  label: PropTypes.node,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
  helperText: PropTypes.node,
  required: PropTypes.bool,
  disabled: PropTypes.bool,
  mode: PropTypes.oneOf(['string', 'seconds']),
};

DurationFormInput.defaultProps = {
  label: null,
  value: '',
  placeholder: '',
  helperText: null,
  required: false,
  disabled: false,
  mode: 'string',
};

// Validation regex
const DURATION_REGEX = /^(\d+)(s|min|h|d|w|mo|y)$/i;

/**
 * Validate if a string matches the duration format
 */
function validateDurationFormat(str) {
  return DURATION_REGEX.test(str.trim());
}

/**
 * Parse duration string to seconds
 * Examples: "2h" -> 7200, "30min" -> 1800
 */
function parseToSeconds(durationStr) {
  const match = durationStr.trim().match(DURATION_REGEX);
  if (!match) return null;

  const amount = parseInt(match[1], 10);
  const unit = match[2].toLowerCase();

  const conversions = {
    's': 1,
    'min': 60,
    'h': 3600,
    'd': 86400,
    'w': 604800,
    'mo': 2592000,  // 30 days
    'y': 31536000,  // 365 days
  };

  return amount * (conversions[unit] || 0);
}

/**
 * Format seconds to human-readable duration string
 * Chooses the largest unit that divides evenly
 * Examples: 7200 -> "2h", 1800 -> "30min", 90 -> "90s"
 */
function formatSecondsToHuman(seconds) {
  if (!seconds || seconds === 0) return '';

  const units = [
    { name: 'y', seconds: 31536000 },
    { name: 'mo', seconds: 2592000 },
    { name: 'w', seconds: 604800 },
    { name: 'd', seconds: 86400 },
    { name: 'h', seconds: 3600 },
    { name: 'min', seconds: 60 },
    { name: 's', seconds: 1 },
  ];

  // Find the largest unit that divides evenly
  for (const unit of units) {
    if (seconds % unit.seconds === 0) {
      const amount = seconds / unit.seconds;
      return `${amount}${unit.name}`;
    }
  }

  // Fallback to seconds if no clean division
  return `${seconds}s`;
}

